import { LOG, display_message_to_user } from '@phi/logging/frontend';
import {
  can_make_edge_nonsmooth,
  can_make_edge_smooth,
  can_smoothen_edges_as_one,
  remove_vertex_smoothness,
  smoothen_edges,
  smoothen_edges_as_one,
  smoothen_edge_wrt_face,
  smoothen_vertices,
  add_joint_smoothness,
  remove_joint_smoothness,
  smoothen_chain_network,
  delete_vertex,
} from './JCore/logging_jcore';
import { uil_find_edge_shared_vertex, uil_get_edges_of_vertex, uil_get_point_at_edge_param, uil_get_vertices_of_edge, uil_is_joint_smooth, uil_is_object_visible } from './ui_logic';
import { suppress_animate, suppress_undo_tracking, unsuppress_animate_if_ready } from './utility';
import { uid_deselect_all_objects } from './ui_display';
import { vertices_of_face } from './lattice';

export function uil_smoothen_vertices(vertex_list, smoothen_vertex_radius) {

  // Remove any 2-vertices.
  var reduced_vertex_list = [... vertex_list];
  for (var i = reduced_vertex_list.length-1; i >= 0; i--) {
    if (uil_get_edges_of_vertex(reduced_vertex_list[i]).length == 2)
    reduced_vertex_list.splice(i, 1);
  }

  var res = smoothen_vertices(reduced_vertex_list, smoothen_vertex_radius);
  return res;
}

export function uil_remove_vertex_smoothness(vertex_list) {
  var cannot_proceed_due_to_fixed_objects = false;
  vertex_list.forEach(function(vid){
    if (uil_is_object_fixed(vid))
      cannot_proceed_due_to_fixed_objects = true;
  });

  if (cannot_proceed_due_to_fixed_objects) {
    display_message_to_user(LOG.WARN, "Cannot un-smoothen a fixed vertex.");
    return;
  }

  var res = remove_vertex_smoothness(vertex_list);
  console_log("Un-smoothen vertices " + vertex_list + " with result: " + res);
  uid_deselect_all_objects();
  return res;
}

export function uil_add_joint_smoothness(edge_pairs, strength, propagate_to_symmetry = true, protect_with_vertices = false) {
  suppress_animate();
  suppress_undo_tracking();
  uid_deselect_all_objects();
  var at_least_one_success = false;

  //Instead of working with the edges we'll use their vertices, as the edge ids will change if we temporarily add vertices on them

  let edge_pair_vertices = []
  edge_pairs.forEach(function(edge_pair) {
    edge_pair_vertices.push([uil_get_vertices_of_edge(edge_pair[0]), uil_get_vertices_of_edge(edge_pair[1])]);
  });


  edge_pair_vertices.forEach(function(edge_pair_vertex_set) {

    var e1id = get_edge_from_vertices(edge_pair_vertex_set[0][0], edge_pair_vertex_set[0][1]);
    var e2id = get_edge_from_vertices(edge_pair_vertex_set[1][0], edge_pair_vertex_set[1][1]);

    if (uil_is_object_fixed(e1id) && uil_is_object_fixed(e2id)) {
      display_message_to_user(LOG.WARN, "Cannot change smoothness between fixed edges, aborting pair.")
    }
    else if (uil_is_joint_smooth(e1id, e2id) == 1) {
      // Pair is already smooth, do nothing.
    }
    else if (uil_is_joint_smooth(e1id, e2id) == 0) {
      // Pair is not smooth, must smoothen it.

      // Check that there is not already smoothness with some other edge, in which case remove it.
      var v = uil_find_edge_shared_vertex(e1id, e2id);
      if (v == 0) {
        display_message_to_user(LOG.WARN, "Edges not sharing a vertex");
      }
      else {
        var edges = uil_get_edges_of_vertex(v);
        var tangency_found = false;
        edges.forEach(function(eid) {
          if (eid != e1id && eid != e2id ) {
            if (uil_is_joint_smooth(eid, e1id) == 1) {
              uil_remove_joint_smoothness([[eid, e1id]], false, propagate_to_symmetry);
              tangency_found = true;
            }
            if (uil_is_joint_smooth(eid, e2id) == 1) {
              uil_remove_joint_smoothness([[eid, e2id]], false, propagate_to_symmetry);
              tangency_found = true;
            }
          }
        })
        if (tangency_found)
          display_message_to_user(LOG.WARN, "At least one the edges was already tangent to another edge outside the selected ones. The existing tangency was removed.");

        // Now add the new tangency
        var res;
        if (protect_with_vertices)
          res = add_joint_smoothness_protected_by_vertices(e1id, e2id, strength, propagate_to_symmetry);
        else
          res = add_joint_smoothness(e1id, e2id, strength, propagate_to_symmetry);
        if (res)
          at_least_one_success = true;
      }
    }
  });

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  return at_least_one_success;
}

export function get_edge_from_vertices(v1, v2) {
  return uil_get_edges_of_vertex(v1).filter(e => uil_get_edges_of_vertex(v2).includes(e));
}

export function add_joint_smoothness_protected_by_vertices(e1id, e2id, strength, propagate_to_symmetry) {
  // Add the vertices
  var cv = uil_find_edge_shared_vertex(e1id, e2id);
  var p1a = uil_get_point_at_edge_param(e1id, 0.0);
  var p1b = uil_get_point_at_edge_param(e1id, 1.0);
  var p2a = uil_get_point_at_edge_param(e2id, 0.0);
  var p2b = uil_get_point_at_edge_param(e2id, 1.0);
  var p1 = 0.05;
  var p2 = 0.05;
  if (uid_vertices.getItem(cv).object3d.position.distanceTo(p1b) < uid_vertices.getItem(cv).object3d.position.distanceTo(p1a))
    p1 = 0.95;
  if (uid_vertices.getItem(cv).object3d.position.distanceTo(p2b) < uid_vertices.getItem(cv).object3d.position.distanceTo(p2a))
    p2 = 0.95;
  var v1 = uil_add_vertex_at_param(e1id, p1);
  var v2 = uil_add_vertex_at_param(e2id, p2);

  const newe1 = get_edge_from_vertices(v1, cv);
  const newe2 = get_edge_from_vertices(v2, cv);

  // Add the joint smoothness
  var res = add_joint_smoothness(newe1, newe2, strength, propagate_to_symmetry);

  // Remove the vertices
  delete_vertex(v1, true);
  delete_vertex(v2, true);

  return res;
}

export function uil_remove_joint_smoothness(edge_pairs, no_geometry_change = false, propagate_to_symmetry = true) {
  suppress_animate();
  uid_deselect_all_objects();
  var at_least_one_success = false;
  var radius = 0;

  suppress_undo_tracking();
  edge_pairs.forEach(function(edge_pair) {
    var e1id = edge_pair[0];
    var e2id = edge_pair[1];

    if (uil_is_joint_smooth(e1id, e2id) == 1) {
      if (uil_is_object_fixed(e1id) && uil_is_object_fixed(e2id)) {
        display_message_to_user(LOG.WARN, "Cannot change smoothness between fixed edges, aborting pair.")
      }
      else {
        // Also remove the fixedness of the edge tangents, in case there is
        let vid = uil_find_edge_shared_vertex(e1id, e2id);
        if (vid) {
          uil_set_edge_fixing_of_tangent_at_vertex(e1id, vid, false);
          uil_set_edge_fixing_of_tangent_at_vertex(e2id, vid, false);
        }
        var res = remove_joint_smoothness(e1id, e2id, no_geometry_change, propagate_to_symmetry);
        if (res)
          at_least_one_success = true;
      }
    }
  });
  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  return at_least_one_success;
}

export function uil_make_edge_tangent_to_face(eid, fid) {
  var common_vertex = null;

  // Make sure that it's a T-junction and the edge is attached to the face.
  var allok = true;

  var esf = uil_get_edges_of_face(fid);
  var vsf = vertices_of_face(fid);
  var vse = uil_get_vertices_of_edge(eid);

  if (esf.indexOf(eid) != -1)
    allok = false;

  if (vsf.indexOf(vse[0]) != -1)
    common_vertex = vse[0];
  else if (vsf.indexOf(vse[1]) != -1)
    common_vertex = vse[1];
  else
    allok = false;

  if (!allok) {
    display_message_to_user(LOG.WARN, "Selected face and edge are not connected.");
    return 0;
  }

  if (!uil_is_object_visible(fid))
    return 0;

  /*
  // Check whether the angle between the edge and the face is less than 45 degrees
  var p0 = uil_get_point_at_edge_param(eid, 0);
  var p1 = uil_get_point_at_edge_param(eid, 1);
  var tan;
  if (point_point_distance(p0, uid_vertices.getItem(common_vertex).object3d.position) <
      point_point_distance(p1, uid_vertices.getItem(common_vertex).object3d.position))
    tan = uil_get_tangent_at_edge_param(eid, 0);
  else
    tan = uil_get_tangent_at_edge_param(eid, 1);

  var b = new THREE.Vector3();
  var d = new THREE.Vector3();
  var n = new THREE.Vector3();
  var wv_r = new WrappedValue(0);
  var wv_f = new WrappedValue(0);
  uil_get_face_geometry(fid, b, n, wv_r, d, wv_f);

  if (tan.angleTo(n) < 45.0/180.0*Math.PI ) {
    display_message_to_user(LOG.WARN, "Face and edge are too far from tangent.");
    return 0;
  }
  */

  suppress_undo_tracking();
  suppress_animate();

  if (uil_is_edge_linear(eid))
    uil_set_linearity_of_edges([eid], false, true);
  var res = smoothen_edge_wrt_face(eid, fid, common_vertex);

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  return res;
}

export function uil_can_make_edge_smooth(eid) {
  // First check if we are in an open surface model and the edge is at the border of the visible surface
  if (uil_is_body_open(uil_get_body_of_edge(eid))) {
    var f_array = uil_get_faces_of_edge(eid);
    if (!uil_is_object_visible(f_array[0]) || !uil_is_object_visible(f_array[1]))
      return 0;
  }

  var res = can_make_edge_smooth(eid);
  if (res == -1)
    display_message_to_user(LOG.WARN, "Error in checking whether edge can be made smooth");
  return res;
}

export function uil_can_make_edge_nonsmooth(eid) {
  var res = can_make_edge_nonsmooth(eid);
  if (res == -1)
    display_message_to_user(LOG.WARN, "Error in checking whether edge can be made non-smooth");
  return res;
}

export function uil_make_edge_smooth(common_edge_id) {
  // Add smoothness between two faces
  var fs = uil_get_faces_of_edge(common_edge_id);
  var es_f1 = uil_get_edges_of_face(fs[0]);
  var es_f2 = uil_get_edges_of_face(fs[1]);

  var vs = uil_get_vertices_of_edge(common_edge_id);
  var es_v1 = uil_get_edges_of_vertex(vs[0]);
  var es_v2 = uil_get_edges_of_vertex(vs[1]);

  var ef1v1, ef1v2, ef2v1, ef2v2;
  es_v1.forEach(function(ev1id) {
    if (ev1id != common_edge_id && es_f1.indexOf(ev1id) != -1)
      ef1v1 = ev1id;
    if (ev1id != common_edge_id && es_f2.indexOf(ev1id) != -1)
      ef2v1 = ev1id;
  });
  es_v2.forEach(function(ev2id) {
    if (ev2id != common_edge_id && es_f1.indexOf(ev2id) != -1)
      ef1v2 = ev2id;
    if (ev2id != common_edge_id && es_f2.indexOf(ev2id) != -1)
      ef2v2 = ev2id;
  });

  var res;
  if ((uil_is_joint_smooth(ef1v1,ef2v1) == 0 && uil_is_joint_smooth(ef1v2,ef2v2) == 0) ||
      (uil_is_joint_smooth(ef1v1,ef2v1) == 1 && uil_is_joint_smooth(ef1v2,ef2v2) == 1))
    res = uid_smoothen_edge_lists_as_one([[ef1v1,ef2v1], [ef1v2,ef2v2]]);
  else if (uil_is_joint_smooth(ef1v2,ef2v2) == 0)
    res = uid_smoothen_edge_lists_as_one([[ef1v2,ef2v2]]);
  else if (uil_is_joint_smooth(ef1v1,ef2v1) == 0)
    res = uid_smoothen_edge_lists_as_one([[ef1v1,ef2v1]]);

  if (res)
    uid_deselect_all_objects();

  return res;
}

export function uil_make_edge_nonsmooth(common_edge_id) {
  // Removes smoothness between two faces
  var fs = uil_get_faces_of_edge(common_edge_id);
  var es_f1 = uil_get_edges_of_face(fs[0]);
  var es_f2 = uil_get_edges_of_face(fs[1]);

  var vs = uil_get_vertices_of_edge(common_edge_id);
  var es_v1 = uil_get_edges_of_vertex(vs[0]);
  var es_v2 = uil_get_edges_of_vertex(vs[1]);

  var ef1v1, ef1v2, ef2v1, ef2v2;
  es_v1.forEach(function(ev1id) {
    if (ev1id != common_edge_id && es_f1.indexOf(ev1id) != -1)
      ef1v1 = ev1id;
    if (ev1id != common_edge_id && es_f2.indexOf(ev1id) != -1)
      ef2v1 = ev1id;
  });
  es_v2.forEach(function(ev2id) {
    if (ev2id != common_edge_id && es_f1.indexOf(ev2id) != -1)
      ef1v2 = ev2id;
    if (ev2id != common_edge_id && es_f2.indexOf(ev2id) != -1)
      ef2v2 = ev2id;
  });

  // Smoothen as one the two pairs of edges
  var res = uid_unsmoothen_edge_lists([[ef1v1,ef2v1],[ef1v2,ef2v2]]);

  if (res)
    uid_deselect_all_objects();

  return res;
}

export function uil_smoothen_chain_of_edges(ids_of_edges_to_smoothen, is_open_list, requested_smoothen_strength = null) {
  // Smoothens a chain of edges that could contain 1 or more edges, open or closed loop,
  // and possibly including 1 or 2 fixed edges (depending on situation)
  // We assume that the edge list is already listified.

  if (uil_is_object_fixed(ids_of_edges_to_smoothen[0]))
    is_open_list = true; // Treat list as open if it contains fixed edges

  if (!requested_smoothen_strength)
    requested_smoothen_strength = global_smoothen_strength;

  var length = ids_of_edges_to_smoothen.length;

  if (length == 0)
    return 0;

  var cannot_proceed_due_to_fixed_objects = false;

  // Check which array elements are fixed
  var first_is_fixed = false;
  var last_is_fixed = false;
  var total_fixed = 0;
  if (uil_is_object_fixed(ids_of_edges_to_smoothen[0]))
    first_is_fixed = true;
  if (uil_is_object_fixed(ids_of_edges_to_smoothen[length-1]))
    last_is_fixed = true;
  ids_of_edges_to_smoothen.forEach(function(eid){
    if (uil_is_object_fixed(eid))
      total_fixed++;
  });

  if (total_fixed == length)
    cannot_proceed_due_to_fixed_objects = true;

  // Process array to position fixed objects as appropriate
  if (is_open_list) {
    if ((total_fixed > 2) ||
        (total_fixed == 2 && (!first_is_fixed || !last_is_fixed) ) ||
        (total_fixed == 1 && !first_is_fixed && !last_is_fixed)
       ) {
      cannot_proceed_due_to_fixed_objects = true;
    }
    else if (last_is_fixed && !first_is_fixed) {
      // must reverse array to bring the fixed object at the beginning.
      ids_of_edges_to_smoothen.reverse();
      first_is_fixed = true;
      last_is_fixed = false;
    }
  }
  else {
    if (total_fixed == 0) {
      // No need to do anything
    }
    else if (total_fixed > 1) {
      cannot_proceed_due_to_fixed_objects = true;
    }
    else if (first_is_fixed) {
      // No need to do anything
    }
    else if (last_is_fixed) {
      // must reverse array to bring the fixed object at the beginning.
      ids_of_edges_to_smoothen.reverse();
      first_is_fixed = true;
      last_is_fixed = false;
    }
    else {
      // Just one is fixed, but it's not the 1st one. Rotate the array to bring it at the beginning.
      var fixed_index = 0;
      for (var i = 0; i < length; i++) {
        if (uil_is_object_fixed(ids_of_edges_to_smoothen[i]))
          fixed_index = i;
      }
      for (var i = 0; i <= fixed_index-1; i++)
        ids_of_edges_to_smoothen.push(ids_of_edges_to_smoothen.shift());
      first_is_fixed = true;
    }
  }
  if (cannot_proceed_due_to_fixed_objects) {
    display_message_to_user(LOG.WARN, "Cannot smoothen edge chaines containing fixed edges execpt at the beginning/end for open loops, or a single fixed edge for closed loops.");
    return 0;
  }
  if (!is_open_list && length < 3) {
    display_message_to_user(LOG.WARN, "Cannot smoothen a closed loop with just two edges.");
    return 0;
  }

  // Prepare an array to hold all non-fixed edges.
  var unfixed_edge_ids = [];

  suppress_undo_tracking();
  suppress_animate();

  var res = 0;
  if (length == 1) {
    res = uil_smoothen_edges(ids_of_edges_to_smoothen, requested_smoothen_strength);
    unfixed_edge_ids.push(ids_of_edges_to_smoothen[0]);
  }
  else if (length == 2) {
    if (first_is_fixed) {
      if (uil_is_joint_smooth(ids_of_edges_to_smoothen[0], ids_of_edges_to_smoothen[1]) == 0)
        // Joint is not smooth, smoothen it.
        res = uil_add_joint_smoothness([[ids_of_edges_to_smoothen[0], ids_of_edges_to_smoothen[1]]],
          global_smoothen_strength /* NO USE SOFT STRENGTH IF ANGLE IS SMALL*/,
          true, false);
      else {
        // Joint is already smooth but may be damaged due to drag, so unsmoothen and resmoothen
        // XXX Note that the uil_smoothen_edges below will probably not fix the damage!!!
        res = uil_smoothen_edges([ids_of_edges_to_smoothen[1]], requested_smoothen_strength);
      }
      unfixed_edge_ids.push(ids_of_edges_to_smoothen[1]);
    }
    else {
      // Perform toggle joint smoothness when there are 2 non-fixed edges
      // NOTE: Check this next line for both NEW_EDGES and OLD_EDGES.
      res = uil_smoothen_edges_as_one(ids_of_edges_to_smoothen, is_open_list, requested_smoothen_strength, false);
    }
  }
  else if (is_open_list) {
    if (first_is_fixed) {
      if (uil_is_joint_smooth(ids_of_edges_to_smoothen[0], ids_of_edges_to_smoothen[1]) == 0)
        // Joint is not smooth, smoothen it.
        res = uil_add_joint_smoothness([[ids_of_edges_to_smoothen[0], ids_of_edges_to_smoothen[1]]], global_smoothen_strength, true, false);
      else {
        // XXX Joint is already smooth but may be damaged due to drag!!!
      }
    }
    if (last_is_fixed) {
      if (uil_is_joint_smooth(ids_of_edges_to_smoothen[length-2], ids_of_edges_to_smoothen[length-1]) == 0)
        // Joint is not smooth, smoothen it.
        res = uil_add_joint_smoothness([[ids_of_edges_to_smoothen[length-2], ids_of_edges_to_smoothen[length-1]]], global_smoothen_strength, true, false);
      else {
        // XXX Joint is already smooth but may be damaged due to drag!!!
      }
    }

    // Make a copy of the array
    var i1 = first_is_fixed ? 1 : 0;
    var i2 = last_is_fixed ? length-1 : length;
    for (var i = i1; i < i2; i++) {
      unfixed_edge_ids.push(ids_of_edges_to_smoothen[i]);
    }

    if (unfixed_edge_ids.length == 1) {
      // Smoothen the only non-fixed edge
      res = uil_smoothen_edges(unfixed_edge_ids, requested_smoothen_strength);
    }
    else {
      res = uil_smoothen_edges_as_one(unfixed_edge_ids, is_open_list, requested_smoothen_strength, false);
    }
  }
  else {
    if (first_is_fixed) {
      if (uil_is_joint_smooth(ids_of_edges_to_smoothen[0], ids_of_edges_to_smoothen[1]) == 0)
        // Joint is not smooth, smoothen it.
        res = uil_add_joint_smoothness([[ids_of_edges_to_smoothen[0], ids_of_edges_to_smoothen[1]]], global_smoothen_strength, true, false);
      else {
        // XXX Joint is already smooth but may be damaged due to drag!!!
      }

      if (uil_is_joint_smooth(ids_of_edges_to_smoothen[0], ids_of_edges_to_smoothen[ids_of_edges_to_smoothen.length-1]) == 0)
        // Joint is not smooth, smoothen it.
        res = uil_add_joint_smoothness([[ids_of_edges_to_smoothen[0], ids_of_edges_to_smoothen[ids_of_edges_to_smoothen.length-1]]], global_smoothen_strength, true, false);
      else {
        // XXX Joint is already smooth but may be damaged due to drag!!!
      }
    }

    // Make a copy of the array
    var i1 = first_is_fixed ? 1 : 0;
    for (var i = i1; i < length; i++) {
      unfixed_edge_ids.push(ids_of_edges_to_smoothen[i]);
    }
    res = uil_smoothen_edges_as_one(unfixed_edge_ids, false, requested_smoothen_strength, false);
  }

  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);
  return res;
}

export function uil_smoothen_edges(ids_of_edges_to_smoothen, requested_smoothen_strength = null) {

  var cannot_proceed_due_to_fixed_objects = false;
  ids_of_edges_to_smoothen.forEach(function(eid){
    if (uil_is_object_fixed(eid))
      cannot_proceed_due_to_fixed_objects = true;
  });

  if (cannot_proceed_due_to_fixed_objects) {
    display_message_to_user(LOG.WARN, "Cannot smoothen fixed edges.");
    return 0;
  }

  suppress_animate();

  var sm = null;
  if (requested_smoothen_strength)
    sm = requested_smoothen_strength;
  else
    sm = global_smoothen_strength;

  // Compute bracketing for first_deriv_opt_weight
  var wtop, wbottom, pc, factor;
  if (sm <= 2) {
    wbottom = 0.001;
    wtop = 0.002;
    pc = sm-1;
  }
  else if (sm <= 3) {
    wbottom = 0.002;
    wtop = 0.005;
    pc = sm-2;
  }
  else if (sm <= 4) {
    wbottom = 0.005;
    wtop = 0.01;
    pc = sm-3;
  }
  else if (sm <= 5) {
    wbottom = 0.01;
    wtop = 0.02;
    pc = sm-4;
  }

  let first_deriv_opt_weight = wbottom * (1-pc) + wtop * pc;

  // Compute bracketing for sec_deriv_opt_weight
  var wtop, wbottom, pc, factor;
  if (sm <= 2) {
    wbottom = 3;
    wtop = 5;
    pc = sm-1;
  }
  else if (sm <= 3) {
    wbottom = 5;
    wtop = 8;
    pc = sm-2;
  }
  else if (sm <= 4) {
    wbottom = 8;
    wtop = 10;
    pc = sm-3;
  }
  else if (sm <= 5) {
    wbottom = 10;
    wtop = 12;
    pc = sm-4;
  }

  let sec_deriv_opt_weight = wbottom * (1-pc) + wtop * pc;

  var force_fix_1st_ders = false;
  var force_fix_2nd_ders = true;

  var res = smoothen_edges(ids_of_edges_to_smoothen, sm, force_fix_1st_ders, force_fix_2nd_ders);

  unsuppress_animate_if_ready();
  return res;
}

export function uil_smoothen_edges_as_one(ids_of_edges_to_smoothen, is_open_list,
  requested_smoothen_strength = global_smoothen_strength, fix_end_ders_override = false) {

  if (ids_of_edges_to_smoothen.length > 12) {
    var split_size = 8;
    // Will split the chain into groups of 8 edges, with 2 overlapping
    var finished = false;
    var starting_index = 0;
    while (!finished) {
      var ending_index = starting_index + split_size - 1;
      var this_split = split_size;
      if (ending_index >= ids_of_edges_to_smoothen.length - 1) {
        ending_index = ids_of_edges_to_smoothen.length - 1;
        finished = true;
      }
      ending_index += 1; // Because end index is not included in slice!
      var tmp_edge_list = ids_of_edges_to_smoothen.slice(starting_index, ending_index);
      if (tmp_edge_list.length >= 2)
        uil_smoothen_edges_as_one(tmp_edge_list, is_open_list, requested_smoothen_strength, fix_end_ders_override);
      starting_index += (this_split - 2);
    }

    return;
  }

  var cannot_proceed_due_to_fixed_objects = false;
  ids_of_edges_to_smoothen.forEach(function(eid){
    if (uil_is_object_fixed(eid))
    cannot_proceed_due_to_fixed_objects = true;
  });


  if (cannot_proceed_due_to_fixed_objects) {
    display_message_to_user(LOG.WARN, "Cannot smoothen a set of edges that includes fixed edges.");
    return;
  }

  if (!is_open_list && ids_of_edges_to_smoothen.length < 3) {
    display_message_to_user(LOG.WARN, "Cannot smoothen a closed loop with just two edges.");
    return;
  }

  suppress_animate();
  suppress_undo_tracking();

  var no_of_edges = ids_of_edges_to_smoothen.length;
  var no_of_tangents = 0;

  // Check if we have tangency between first edge and outside edges, given that it's an open list of edges
  if (is_open_list) {

    var vFirst = 0;
    var vLast = 0;
    var first_edge_id = ids_of_edges_to_smoothen[0];
    var second_edge_id = ids_of_edges_to_smoothen[1];
    var one_before_last_edge_id = ids_of_edges_to_smoothen[no_of_edges-2];
    var last_edge_id = ids_of_edges_to_smoothen[no_of_edges-1];

    var vfs = uil_find_edge_shared_vertex(first_edge_id, second_edge_id);
    var vol = uil_find_edge_shared_vertex(one_before_last_edge_id, last_edge_id);

    var vs = uil_get_vertices_of_edge(first_edge_id);
    vFirst = (vfs == vs[0] ? vs[1] : vs[0]);
    var vs = uil_get_vertices_of_edge(last_edge_id);
    vLast = (vol == vs[0] ? vs[1] : vs[0]);

    var esA = uil_get_edges_of_vertex(vFirst);
    var esB = uil_get_edges_of_vertex(vLast);

    var tangent_at_beginning = 0;
    esA.forEach(function(eAid) {
      if (eAid != first_edge_id && uil_is_joint_smooth(eAid, first_edge_id) == 1)
        tangent_at_beginning = 1;
    });

    var tangent_at_end = 0;
    esB.forEach(function(eBid) {
      if (eBid != last_edge_id && uil_is_joint_smooth(eBid, last_edge_id) == 1)
        tangent_at_end = 1;
    });

    // Remove tangencies at beginning and end if requested.
    var breakTangency = App.state.break_terminal_tangencies;
    if (breakTangency) {
      esA.forEach(function(eAid) {
        if (eAid != first_edge_id)
          uil_remove_joint_smoothness([[eAid, first_edge_id]], false, true);
      });
      var tangent_at_end = 0;
      esB.forEach(function(eBid) {
        if (eBid != last_edge_id)
          uil_remove_joint_smoothness([[eBid, last_edge_id]], false, true);
      });
      tangent_at_beginning = 0;
      tangent_at_end = 0;
    }
    var no_of_tangents = tangent_at_beginning + tangent_at_end;
  }

  var weight;

  if (no_of_tangents == 0) {
    if (no_of_edges == 2)
      weight = 850;
    else if (no_of_edges == 3)
      weight = 650;
    else if (no_of_edges == 4)
      weight = 550;
    else if (no_of_edges == 5)
      weight = 450;
    else if (no_of_edges == 6)
      weight = 250;
    else if (no_of_edges == 7)
      weight = 200;
    else
      weight = 130;
  }
  else if (no_of_tangents == 1) {
    if (no_of_edges == 2)
      weight = 800;
    else if (no_of_edges == 3)
      weight = 400;
    else if (no_of_edges == 4)
      weight = 250;
    else if (no_of_edges == 5)
      weight = 230;
    else if (no_of_edges == 6)
      weight = 200;
    else if (no_of_edges == 7)
      weight = 175;
    else
      weight = 125;
  }
  else { // no_of_tangents == 2
    if (no_of_edges == 2)
      weight = 500;
    else if (no_of_edges == 3)
      weight = 250;
    else if (no_of_edges == 4)
      weight = 180;
    else if (no_of_edges == 5)
      weight = 75;
    else if (no_of_edges == 6)
      weight = 65;
    else if (no_of_edges == 7)
      weight = 50;
    else
      weight = 40;
  }

  var sm = null;
  if (requested_smoothen_strength)
    sm = requested_smoothen_strength;
  else
    sm = global_smoothen_strength;

  // Adjust overall regular weight strenghts by some factor, based on usage and experimentation.
  // weight *= 2.0;

  // Compute bracketing weights based on regular weight

  var wtop, wbottom, pc, factor;
  if (sm <= 2) {
    wbottom = 4;
    wtop = 2;
    pc = sm-1;
  }
  else if (sm <= 3) {
    wbottom = 2;
    wtop = 1;
    pc = sm-2;
  }
  else if (sm <= 4) {
    wbottom = 1;
    wtop = 0.5;
    pc = sm-3;
  }
  else if (sm <= 5) {
    wbottom = 0.5;
    wtop = 0.17;
    pc = sm-4;
  }

  factor = wbottom * (1-pc) + wtop * pc;
  weight *= factor;

  var strictly_fix_end_ders = false;

  var res = smoothen_edges_as_one(ids_of_edges_to_smoothen, requested_smoothen_strength, strictly_fix_end_ders, fix_end_ders_override);

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  set_button_states();
  return res;
}

export function uil_can_smoothen_edges_as_one(ids_of_edges_to_smoothen) {
  return can_smoothen_edges_as_one(ids_of_edges_to_smoothen);
}

export function uil_smoothen_chain_network(ids_of_edges_to_smoothen, graph_distance) {
  var res = smoothen_chain_network(ids_of_edges_to_smoothen, graph_distance, global_smoothen_strength);
  set_button_states();
  return res;
}
